import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Alert, Snackbar } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Item, CreateItemRequest, UpdateItemRequest, SearchParams, Category, CreateCategoryRequest, UpdateCategoryRequest } from '../types';
import { apiClient } from '../services/api';
import { ItemList, ItemDialog, DeleteItemDialog, BulkUploadDialog } from '../features/catalog';
import { ItemListRef } from '../features/catalog';
import { CategoryDialog, DeleteCategoryDialog, CategoryManagementDialog } from '../features/catalog';
import { useLanguage } from '../contexts/LanguageContext';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import { useItemMutations } from '../hooks/useItemMutations';
import { errorMessage } from '../utils/errorMessage';
import { useAuth } from '../contexts/AuthContext';
import CompanyScopeBanner from '../components/common/CompanyScopeBanner';

const fetchItemsPage = (params: SearchParams) => apiClient.getItemsPaginated(params);

const Items: React.FC = () => {
  const { t } = useLanguage();
  const { user: currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const itemListRef = useRef<ItemListRef>(null);
  const initialQuery = new URLSearchParams(location.search);
  const shouldFocusSearch = initialQuery.get('focus') === 'search';
  const [searchParams, setSearchParams] = useState<SearchParams>({
    page: 0,
    size: 10,
    sort: 'name',
    direction: 'asc',
    name: initialQuery.get('barcode') || undefined,
    companyId: initialQuery.get('companyId') || undefined,
  });

  useEffect(() => {
    const next = new URLSearchParams();
    if (searchParams.companyId) next.set('companyId', searchParams.companyId);
    if (searchParams.name) next.set('name', searchParams.name);
    if (searchParams.page !== undefined) next.set('page', String(searchParams.page));
    if (searchParams.size !== undefined) next.set('size', String(searchParams.size));
    if (searchParams.sort) next.set('sort', searchParams.sort);
    if (searchParams.direction) next.set('direction', searchParams.direction);
    const nextSearch = next.toString();
    if (nextSearch !== location.search.replace(/^\?/, '')) {
      navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true });
    }
  }, [location.pathname, location.search, navigate, searchParams]);

  // Dialog states
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);

  // Category management states
  const [categoryDialog, setCategoryDialog] = useState<{
    open: boolean;
    category?: Category | null;
  }>({
    open: false,
    category: null,
  });

  const [deleteCategoryDialog, setDeleteCategoryDialog] = useState<{
    open: boolean;
    category: Category | null;
  }>({
    open: false,
    category: null,
  });

  const [categoryManagementDialog, setCategoryManagementDialog] = useState(false);
  const [categoryRefreshTrigger, setCategoryRefreshTrigger] = useState(0);

  // Notification states
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Auto-open add dialog if coming from quick action
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('action') === 'add') {
      handleAddItem();
      // Clean up URL without page reload
      const next = new URLSearchParams(location.search);
      next.delete('action');
      next.delete('focus');
      navigate({ pathname: '/items', search: next.toString() ? `?${next}` : '' }, { replace: true });
    }
  }, [location, navigate]);

  // Focus search when entered through the browse-items quick action.
  useEffect(() => {
    if (!shouldFocusSearch) return;
    const timer = setTimeout(() => {
      itemListRef.current?.focusSearch();
    }, 100);

    return () => clearTimeout(timer);
  }, [shouldFocusSearch]);

  // Notification helper
  const showNotification = useCallback((message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setNotification({ open: true, message, severity });
  }, []);

  const hideNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  const itemsQuery = usePaginatedQuery(searchParams, fetchItemsPage, t('loading.failedToLoad'), 'name', true, 'items');
  const {
    saveItemMutation,
    deleteItemMutation,
    saveCategoryMutation,
    deleteCategoryMutation,
  } = useItemMutations();

  useEffect(() => {
    if (itemsQuery.error) {
      showNotification(itemsQuery.error, 'error');
    }
  }, [itemsQuery.error, showNotification]);

  // Handle search
  const handleSearch = (term: string) => {
    setSearchParams(prev => ({
      ...prev,
      name: term || undefined,
      page: 0 // Reset to first page when searching
    }));
  };

  // Handle search params change (for filters)
  const handleSearchParamsChange = (updates: Partial<SearchParams>) => {
    setSearchParams(prev => ({
      ...prev,
      ...updates
    }));
  };

  // Handle page change
  // Keep legacy handlers for backward compatibility
  const handlePageChange = (page: number) => {
    setSearchParams(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (size: number) => {
    setSearchParams(prev => ({ ...prev, size, page: 0 }));
  };

  // Handle sort change
  const handleSortChange = (field?: string, direction?: 'asc' | 'desc') => {
    setSearchParams(prev => ({ ...prev, sort: field, direction, page: 0 }));
  };

  // Handle company filter change
  const handleCompanyFilter = (companyId: string) => {
    setSearchParams(prev => ({
      ...prev,
      companyId: companyId || undefined,
      page: 0 // Reset to first page when filtering
    }));
  };

  // Handle add item
  const handleAddItem = () => {
    setSelectedItem(null);
    setItemDialogOpen(true);
  };

  // Handle edit item
  const handleEditItem = async (item: Item) => {
    try {
      // Fetch full item details including categories
      const fullItem = await apiClient.getItem(item.id);
      setSelectedItem(fullItem);
      setItemDialogOpen(true);
    } catch (error: unknown) {
      showNotification(errorMessage(error, t('loading.failedToLoad')), 'error');
    }
  };

  // Handle delete item (open dialog)
  const handleDeleteItem = (item: Item) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  // Handle bulk upload
  const handleBulkUpload = () => {
    setBulkUploadDialogOpen(true);
  };

  // Handle bulk upload success
  const handleBulkUploadSuccess = () => {
    itemsQuery.reload();
    showNotification(t('bulkUpload.uploadComplete'));
  };

  // Category management handlers
  const handleAddCategory = () => {
    setCategoryManagementDialog(true);
  };

  const handleAddCategoryFromManagement = () => {
    setCategoryManagementDialog(false);
    setCategoryDialog({
      open: true,
      category: null,
    });
  };

  const handleEditCategory = (category: Category) => {
    setCategoryManagementDialog(false);
    setCategoryDialog({
      open: true,
      category,
    });
  };

  const handleDeleteCategory = (category: Category) => {
    setCategoryManagementDialog(false);
    setDeleteCategoryDialog({
      open: true,
      category,
    });
  };

  const handleCategorySubmit = async (data: CreateCategoryRequest | UpdateCategoryRequest) => {
    try {
      await saveCategoryMutation.mutateAsync({ id: categoryDialog.category?.id, data });
      showNotification(categoryDialog.category ? t('category.updated') : t('category.created'));

      setCategoryDialog({ open: false, category: null });
      setCategoryManagementDialog(true);
      // Refresh both the items list and category management dialog
      if (itemListRef.current) {
        itemListRef.current.refresh();
      }
      setCategoryRefreshTrigger(prev => prev + 1);
    } catch (error: unknown) {
      showNotification(errorMessage(error, 'Category operation failed'), 'error');
    }
  };

  const handleConfirmDeleteCategory = async () => {
    if (!deleteCategoryDialog.category) return;

    try {
      await deleteCategoryMutation.mutateAsync(deleteCategoryDialog.category.id);
      showNotification(t('category.deleted'));
      setDeleteCategoryDialog({ open: false, category: null });
      setCategoryManagementDialog(true);
      // Refresh both the items list and category management dialog
      if (itemListRef.current) itemListRef.current.refresh();
      setCategoryRefreshTrigger(prev => prev + 1);
    } catch (error: unknown) {
      showNotification(errorMessage(error, 'Failed to delete category'), 'error');
    }
  };

  // Handle item form submission
  const handleItemSubmit = async (data: CreateItemRequest | UpdateItemRequest) => {
    try {
      await saveItemMutation.mutateAsync({ id: selectedItem?.id, data });
      showNotification(selectedItem ? t('items.updated') : t('items.created'));

      setItemDialogOpen(false);
      // Refresh categories in case new ones were created
      if (itemListRef.current) {
        itemListRef.current.refresh();
      }
      setCategoryRefreshTrigger(prev => prev + 1);
    } catch (error: unknown) {
      showNotification(errorMessage(error, 'Failed to save item'), 'error');
      throw error; // Re-throw to let the form handle it
    }
  };

  // Handle item deletion confirmation
  const handleDeleteConfirm = async (item: Item) => {
    try {
      await deleteItemMutation.mutateAsync(item.id);
      showNotification(t('items.deleted'));
      setDeleteDialogOpen(false);
    } catch (error: unknown) {
      showNotification(errorMessage(error, 'Failed to delete item'), 'error');
    }
  };

  // Handle refresh from ItemList (for inventory updates)
  const handleRefresh = useCallback(() => {
    itemsQuery.reload();
  }, [itemsQuery]);

  // Handle dialog close
  const handleDialogClose = () => {
    if (!saveItemMutation.isPending && !deleteItemMutation.isPending) {
      setItemDialogOpen(false);
      setDeleteDialogOpen(false);
      setBulkUploadDialogOpen(false);
      setSelectedItem(null);
    }
  };

  return (
    <Container maxWidth={false} disableGutters sx={{ pt: 0, pb: 2 }}>
      <CompanyScopeBanner companyId={searchParams.companyId} />
      {/* Items List */}
      <ItemList
        ref={itemListRef}
        paginatedData={itemsQuery.data}
        loading={itemsQuery.isLoading}
        searchParams={searchParams}
        onSearch={handleSearch}
        onSearchParamsChange={handleSearchParamsChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSortChange={handleSortChange}
        onEdit={handleEditItem}
        onDelete={handleDeleteItem}
        onAdd={handleAddItem}
        onBulkUpload={handleBulkUpload}
        onCompanyFilter={handleCompanyFilter}
        onAddCategory={handleAddCategory}
        onEditCategory={handleEditCategory}
        onDeleteCategory={handleDeleteCategory}
        onRefresh={handleRefresh}
        autoFocusSearch={shouldFocusSearch}
        batchTrackingEnabled={currentUser?.batchTrackingEnabled}
      />

      {/* Item Dialog (Add/Edit) */}
      <ItemDialog
        open={itemDialogOpen}
        item={selectedItem}
        onClose={handleDialogClose}
        onSubmit={handleItemSubmit}
        priceEntryMode={currentUser?.priceEntryMode}
        defaultVatRate={currentUser?.defaultVatRate}
        batchTrackingEnabled={currentUser?.batchTrackingEnabled}
        isLoading={saveItemMutation.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteItemDialog
        open={deleteDialogOpen}
        item={selectedItem}
        onClose={handleDialogClose}
        onConfirm={handleDeleteConfirm}
        isLoading={deleteItemMutation.isPending}
      />

      {/* Bulk Upload Dialog */}
      <BulkUploadDialog
        open={bulkUploadDialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleBulkUploadSuccess}
      />

      {/* Category Dialog (Add/Edit) */}
      <CategoryDialog
        open={categoryDialog.open}
        category={categoryDialog.category}
        onClose={() => setCategoryDialog({ open: false, category: null })}
        onSubmit={handleCategorySubmit}
        isLoading={saveCategoryMutation.isPending}
      />

      {/* Delete Category Dialog */}
      <DeleteCategoryDialog
        open={deleteCategoryDialog.open}
        category={deleteCategoryDialog.category}
        onClose={() => setDeleteCategoryDialog({ open: false, category: null })}
        onConfirm={handleConfirmDeleteCategory}
        isLoading={deleteCategoryMutation.isPending}
      />

      {/* Category Management Dialog */}
      <CategoryManagementDialog
        open={categoryManagementDialog}
        onClose={() => setCategoryManagementDialog(false)}
        onAddCategory={handleAddCategoryFromManagement}
        onEditCategory={handleEditCategory}
        onDeleteCategory={handleDeleteCategory}
        refreshTrigger={categoryRefreshTrigger}
      />

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={hideNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={hideNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Items;
